import useTranslation from '@/feature/lang/hook';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isEqual,
  startOfDay,
  startOfMinute,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subHours,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import Translate from '../Translate';
import type { DateRange, Preset, PresetKeys, PresetOption } from './types';
import { getRange } from './utils';

const HOUR = 60 * 60;
const DAY = 24 * HOUR;

export const presets: Record<string, Preset> = {
  last2hours: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate
          text={texts.component.dateRange.preset.lastXhours.label}
          values={{ numberOfHours: 2 }}
        />
      );
    },
    fn: () => ({
      from: subHours(new Date(), 2),
      to: new Date(),
    }),
    seconds: 2 * HOUR,
  },
  last6hours: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate
          text={texts.component.dateRange.preset.lastXhours.label}
          values={{ numberOfHours: 6 }}
        />
      );
    },
    fn: () => ({
      from: subHours(new Date(), 6),
      to: new Date(),
    }),
    seconds: 6 * HOUR,
  },
  last12hours: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate
          text={texts.component.dateRange.preset.lastXhours.label}
          values={{ numberOfHours: 12 }}
        />
      );
    },
    fn: () => ({
      from: subHours(new Date(), 12),
      to: new Date(),
    }),
    seconds: 12 * HOUR,
  },
  last24hours: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate
          text={texts.component.dateRange.preset.lastXhours.label}
          values={{ numberOfHours: 24 }}
        />
      );
    },
    fn: () => ({
      from: subHours(new Date(), 24),
      to: new Date(),
    }),
    seconds: DAY,
  },
  last7days: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate
          text={texts.component.dateRange.preset.lastXdays.label}
          values={{ numberOfDays: 7 }}
        />
      );
    },
    fn: () => ({
      from: startOfDay(subDays(new Date(), 6)),
      to: endOfDay(new Date()),
    }),
    seconds: 7 * DAY,
  },
  last30days: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate
          text={texts.component.dateRange.preset.lastXdays.label}
          values={{ numberOfDays: 30 }}
        />
      );
    },
    fn: () => ({
      from: startOfDay(subDays(new Date(), 29)),
      to: endOfDay(new Date()),
    }),
    seconds: 30 * DAY,
  },
  last90days: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate
          text={texts.component.dateRange.preset.lastXdays.label}
          values={{ numberOfDays: 90 }}
        />
      );
    },
    fn: () => ({
      from: startOfDay(subDays(new Date(), 89)),
      to: endOfDay(new Date()),
    }),
    seconds: 90 * DAY,
  },
  today: {
    Label: () => {
      const { texts } = useTranslation();
      return <Translate text={texts.component.dateRange.preset.today.label} />;
    },
    fn: () => ({
      from: startOfDay(new Date()),
      to: endOfDay(new Date()),
    }),
    seconds: DAY,
  },
  yesterday: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate text={texts.component.dateRange.preset.yesterday.label} />
      );
    },
    fn: () => ({
      from: startOfDay(subDays(new Date(), 1)),
      to: endOfDay(subDays(new Date(), 1)),
    }),
    seconds: DAY,
  },
  week: {
    Label: () => {
      const { texts } = useTranslation();
      return <Translate text={texts.component.dateRange.preset.week.label} />;
    },
    fn: () => ({
      from: startOfWeek(new Date(), { weekStartsOn: 1 }),
      to: endOfWeek(new Date(), { weekStartsOn: 1 }),
    }),
    seconds: 7 * DAY,
  },
  prevWeek: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate text={texts.component.dateRange.preset.prevWeek.label} />
      );
    },
    fn: () => {
      const date = subWeeks(new Date(), 1);
      return {
        from: startOfWeek(date, { weekStartsOn: 1 }),
        to: endOfWeek(date, { weekStartsOn: 1 }),
      };
    },
    seconds: 7 * DAY,
  },
  month: {
    Label: () => {
      const { texts } = useTranslation();
      return <Translate text={texts.component.dateRange.preset.month.label} />;
    },
    fn: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()) }),
    seconds: 31 * DAY,
  },
  prevMonth: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate text={texts.component.dateRange.preset.prevMonth.label} />
      );
    },
    fn: () => {
      const date = subMonths(new Date(), 1);
      return {
        from: startOfMonth(date),
        to: endOfMonth(date),
      };
    },
    seconds: 31 * DAY,
  },
  year: {
    Label: () => {
      const { texts } = useTranslation();
      return <Translate text={texts.component.dateRange.preset.year.label} />;
    },
    fn: () => ({ from: startOfYear(new Date()), to: endOfYear(new Date()) }),
    seconds: 365 * DAY,
  },
  prevYear: {
    Label: () => {
      const { texts } = useTranslation();
      return (
        <Translate text={texts.component.dateRange.preset.prevYear.label} />
      );
    },
    fn: () => {
      const date = subYears(new Date(), 1);
      return {
        from: startOfYear(date),
        to: endOfYear(date),
      };
    },
    seconds: 365 * DAY,
  },
};

export const presetOptions: PresetOption[] = Object.entries(presets).map(
  ([key, { Label, fn, seconds }]) => ({
    value: key as PresetKeys,
    label: <Label />,
    fn,
    seconds,
  })
);

export function getActivePreset(
  value: string[] | DateRange,
  options = presetOptions
) {
  const range = getRange(value, { modifier: startOfMinute });
  if (!(range && range.from && range.to)) {
    return;
  }

  return options.find((opt) => {
    const { from, to } = opt.fn();

    return (
      isEqual(range.from, startOfMinute(from)) &&
      isEqual(range.to, startOfMinute(to))
    );
  });
}
